/**
 * Hook para obtener datos filtrados según el scope del usuario
 * NUEVO MODELO: Roles basados en email
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { clubService } from "@/lib/services/organization";
import type { Club } from "@/types";

/**
 * Hook para obtener los clubes accesibles por el usuario actual
 * - Director de club: solo su club
 * - Admin asociación: clubes de su asociación
 * - Admin unión: todos los clubes
 * - Member: ninguno (no gestiona clubes)
 */
export function useAccessibleClubs() {
  const { profile } = useAuthStore();
  const { role, isClubDirector } = usePermissions();

  return useQuery({
    queryKey: [
      "accessible-clubs",
      profile?.id,
      role,
      profile?.directorOfClub?.id,
      profile?.scopeId, // Incluir scopeId para asociaciones
    ],
    queryFn: async (): Promise<Club[]> => {
      console.log(
        "[useAccessibleClubs] Fetching clubs for role:",
        role,
        "scopeId:",
        profile?.scopeId,
      );

      // Director de club: retornar solo su club
      if (isClubDirector && profile?.directorOfClub) {
        return clubService
          .getById(profile.directorOfClub.id)
          .then((club) => (club ? [club] : []));
      }

      // Admin de unión: todos los clubes
      if (role === "admin_union") {
        return clubService.getAll();
      }

      // Admin de asociación: clubes de su asociación
      if (role === "admin_association" && profile?.scopeId) {
        const clubs = await clubService.getByAssociation(profile.scopeId);
        console.log("[useAccessibleClubs] Found clubs:", clubs.length, clubs);
        return clubs;
      }

      // Member no gestiona clubes
      return [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para obtener el club activo actual
 * Para directores es su club asignado
 * Para admins puede venir de un selector en la UI
 */
export function useActiveClub() {
  const { profile } = useAuthStore();
  const { isClubDirector } = usePermissions();

  return useMemo(() => {
    if (isClubDirector && profile?.directorOfClub) {
      return profile.directorOfClub;
    }
    // Para admins, el club activo puede venir de un selector en la UI (future)
    return null;
  }, [profile?.directorOfClub, isClubDirector]);
}

/**
 * Hook para verificar si el usuario tiene acceso a un club específico
 * @param clubId - ID del club a verificar
 * @param clubAssociationId - ID de la asociación del club (opcional, para evitar query adicional)
 */
export function useClubAccess(
  clubId: string | undefined,
  clubAssociationId?: string,
) {
  const { profile } = useAuthStore();
  const { role, isClubDirector } = usePermissions();

  return useMemo(() => {
    if (!clubId) return false;

    // Admin de unión tiene acceso a todo
    if (role === "admin_union") return true;

    // Director de club: verificar si es SU club
    if (isClubDirector && profile?.directorOfClub) {
      return profile.directorOfClub.id === clubId;
    }

    // Admin de asociación: verificar si el club está en su asociación
    if (role === "admin_association" && profile?.scopeId) {
      // Si tenemos el associationId del club, verificamos directamente
      if (clubAssociationId) {
        return clubAssociationId === profile.scopeId;
      }
      // Si no tenemos el associationId, asumimos acceso
      // (el llamador debería pasar clubAssociationId para verificación precisa)
      return true;
    }

    return false;
  }, [
    clubId,
    clubAssociationId,
    role,
    isClubDirector,
    profile?.directorOfClub,
    profile?.scopeId,
  ]);
}

/**
 * Hook async para verificar acceso a un club (con query de datos)
 */
export function useClubAccessAsync(clubId: string | undefined) {
  const { profile } = useAuthStore();
  const { role, isClubDirector } = usePermissions();

  return useQuery({
    queryKey: ["club-access", clubId, role, profile?.scopeId],
    queryFn: async (): Promise<boolean> => {
      if (!clubId) return false;

      // Admin de unión tiene acceso a todo
      if (role === "admin_union") return true;

      // Director de club: verificar si es SU club
      if (isClubDirector && profile?.directorOfClub) {
        return profile.directorOfClub.id === clubId;
      }

      // Admin de asociación: verificar jerarquía
      if (role === "admin_association" && profile?.scopeId) {
        const club = await clubService.getById(clubId);
        if (!club) return false;
        return club.associationId === profile.scopeId;
      }

      return false;
    },
    enabled: !!clubId,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
}

// Gestión de iglesias: No implementado porque la app actual
// se enfoca en clubes. Reimplementar cuando se necesite.

/**
 * Hook para obtener los IDs de clubes accesibles (útil para queries)
 */
export function useAccessibleClubIds(): string[] {
  const { profile } = useAuthStore();
  const { isClubDirector } = usePermissions();

  return useMemo(() => {
    if (isClubDirector && profile?.directorOfClub) {
      return [profile.directorOfClub.id];
    }
    // Para admins, retornar array vacío (significa "todos")
    return [];
  }, [isClubDirector, profile?.directorOfClub]);
}

/**
 * Hook que retorna el clubId para usar en queries
 * Para directores de club retorna su club
 * Para admins puede ser null (ver todos)
 */
export function useQueryClubId(): string | null {
  const { profile } = useAuthStore();
  const { isClubDirector } = usePermissions();

  if (isClubDirector && profile?.directorOfClub) {
    return profile.directorOfClub.id;
  }

  // Para admins, retornar null (ven todos)
  return null;
}

/**
 * Contexto de datos para pasar a servicios
 */
export interface DataContext {
  clubId: string | null;
  clubIds: string[];
  isRestricted: boolean; // true si el usuario tiene scope limitado
}

export function useDataContext(): DataContext {
  const { profile } = useAuthStore();
  const { isClubDirector, role } = usePermissions();

  return useMemo(() => {
    const isRestricted = isClubDirector;

    return {
      clubId:
        isClubDirector && profile?.directorOfClub
          ? profile.directorOfClub.id
          : null,
      clubIds:
        isClubDirector && profile?.directorOfClub
          ? [profile.directorOfClub.id]
          : [],
      isRestricted,
    };
  }, [profile?.directorOfClub, isClubDirector, role]);
}

/**
 * Alias para useDataContext - proporciona el scope de datos del usuario
 */
export const useDataScope = useDataContext;
